import React, { useEffect, useState, useRef } from 'react';
import { Toast, ToastContainer } from 'react-bootstrap';

const AUTO_HIDE_DELAY = 5000; // 5 seconds

// Notification sound - a pleasant chime
const playNotificationSound = () => {
  try {
    // Create an audio context for a simple notification sound
    const audioContext = new (window.AudioContext || window.webkitAudioContext)();
    const oscillator = audioContext.createOscillator();
    const gainNode = audioContext.createGain();

    oscillator.connect(gainNode);
    gainNode.connect(audioContext.destination);

    // Pleasant notification tone
    oscillator.frequency.setValueAtTime(880, audioContext.currentTime); // A5 note
    oscillator.frequency.setValueAtTime(1100, audioContext.currentTime + 0.1); // C#6 note
    oscillator.type = 'sine';

    // Fade out
    gainNode.gain.setValueAtTime(0.3, audioContext.currentTime);
    gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.3);

    oscillator.start(audioContext.currentTime);
    oscillator.stop(audioContext.currentTime + 0.3);
  } catch (e) {
    // Silently fail if audio is not supported
  }
};

/**
 * NotificationToast component - Shows toast notification for new notifications
 * @param {Object} props
 * @param {Object} props.notification - The new notification to display
 * @param {Function} props.onClose - Callback when toast is closed
 */
function NotificationToast({ notification, onClose }) {
  const [show, setShow] = useState(false);
  const prevNotificationId = useRef(null);

  useEffect(() => {
    if (notification && notification.Id !== prevNotificationId.current) {
      prevNotificationId.current = notification.Id;
      setShow(true);
      playNotificationSound();
    }
  }, [notification]);

  const handleClose = () => {
    setShow(false);
    onClose?.();
  };

  if (!notification) return null;

  return (
    <ToastContainer
      position="top-end"
      className="p-3"
      style={{ zIndex: 9999 }}
    >
      <Toast
        show={show}
        onClose={handleClose}
        delay={AUTO_HIDE_DELAY}
        autohide
        className="notification-toast"
      >
        <Toast.Header className="bg-primary text-white">
          <i className="fa fa-bell me-2" />
          <strong className="me-auto">New Notification</strong>
          <small>just now</small>
        </Toast.Header>
        <Toast.Body>
          <div className="fw-semibold">{notification.Subject}</div>
          {notification.BodyType === 'text' && notification.Body && (
            <small className="text-muted d-block text-truncate" style={{ maxWidth: '280px' }}>
              {notification.Body.substring(0, 100)}
              {notification.Body.length > 100 ? '...' : ''}
            </small>
          )}
        </Toast.Body>
      </Toast>

      <style>
        {`
          .notification-toast {
            min-width: 300px;
            box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
          }
          /* Fit within the viewport on small screens (toast sits top-end with p-3) */
          @media (max-width: 575.98px) {
            .notification-toast {
              min-width: 0;
              width: calc(100vw - 2rem);
              max-width: calc(100vw - 2rem);
            }
          }
          .notification-toast .toast-header {
            border-bottom: 1px solid rgba(255, 255, 255, 0.2);
          }
          .notification-toast .toast-header .btn-close {
            filter: brightness(0) invert(1);
          }
        `}
      </style>
    </ToastContainer>
  );
}

export default NotificationToast;
